import React, { useCallback } from 'react';
import { Spinner } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { EmptyContentClientIc } from '../../../../components/icons';
import { useTranslations } from '../../../../contexts/translation.context';
import { TransactionsTableProps, TransactionData } from '../types';
import { TABLE_COLUMNS, getTableColumns } from '../constants';
import {
  TransactionIdCell,
  TransactionCampaignCell,
  TransactionUserCell,
  TransactionAmountCell,
  TransactionDateCell,
  TransactionStatusCell,
} from './index';

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  transactions,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  transactionTotal,
}) => {
  const { t } = useTranslations();
  const renderCell = useCallback((transaction: TransactionData, columnKey: string) => {
    switch (columnKey) {
      case 'id':
        return <TransactionIdCell transaction={transaction} />;
      case 'campaign':
        return <TransactionCampaignCell transaction={transaction} />;
      case 'user':
        return <TransactionUserCell transaction={transaction} />;
      case 'status':
        return <TransactionStatusCell transaction={transaction} />;
      case 'amount':
        return <TransactionAmountCell transaction={transaction} />;
      case 'createdAt':
        return <TransactionDateCell transaction={transaction} />;
      default:
        return null;
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <UniversalTable
      data={transactions}
      columns={getTableColumns(t, transactionTotal)}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentClientIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('transactions.table.empty.title')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('transactions.table.empty.description')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default TransactionsTable;
